import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Rocket, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { WebsiteList } from '@/components/WebsiteList';
import { WebsiteDetails } from '@/components/WebsiteDetails';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NetlifyBuilder = () => {
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [showNewSite, setShowNewSite] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [netlifyToken, setNetlifyToken] = useState('');
  const [newSite, setNewSite] = useState({ name: '', description: '', use_ai: true });

  const loadWebsites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/netlify/websites`);
      setWebsites(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Error loading websites:', error);
    }
  }, []);

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites]);

  const generateWebsite = async (e) => {
    e.preventDefault();
    if (!newSite.name.trim()) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/netlify/websites`, newSite);
      toast.success('Website generated by AI!');
      toast.info(`Created ${response.data.files_generated} files ready to deploy!`);
      setNewSite({ name: '', description: '', use_ai: true });
      setShowNewSite(false);
      loadWebsites();
      const fullWebsite = await axios.get(`${API}/netlify/websites/${response.data.website_id}`);
      setSelectedWebsite(fullWebsite.data);
    } catch (error) {
      toast.error('Failed to generate website');
    } finally {
      setGenerating(false);
    }
  };

  const deployToNetlify = async (websiteId) => {
    setDeploying(true);
    try {
      const response = await axios.post(`${API}/netlify/deploy`, {
        website_id: websiteId,
        netlify_token: netlifyToken || undefined
      });
      if (response.data.status === 'token_required') {
        toast.error('Netlify Token Required!');
        toast.info('Get free token at Netlify.com', { duration: 7000 });
        return;
      }
      if (response.data.status === 'deployed') {
        toast.success('Deployed to Netlify!');
        toast.success(`Live at: ${response.data.url}`, { duration: 10000 });
        loadWebsites();
        const updated = await axios.get(`${API}/netlify/websites/${websiteId}`);
        setSelectedWebsite(updated.data);
      }
    } catch (error) {
      toast.error('Deployment failed');
    } finally {
      setDeploying(false);
    }
  };

  const deleteWebsite = async (websiteId) => {
    try {
      await axios.delete(`${API}/netlify/websites/${websiteId}`);
      toast.success('Website deleted');
      loadWebsites();
      if (selectedWebsite?.id === websiteId) {
        setSelectedWebsite(null);
      }
    } catch (error) {
      toast.error('Failed to delete website');
    }
  };

  return (
    <div className='h-full flex flex-col bg-black' data-testid='netlify-builder-panel'>
      <div className='border-b border-border p-6 bg-surface'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            <Globe size={32} weight='duotone' className='text-foreground' />
            <div>
              <h2 className='text-2xl font-bold tracking-tight' data-testid='netlify-header-title'>AI Website Builder</h2>
              <p className='text-sm text-muted-foreground'>Build & deploy websites to Netlify - powered by AI</p>
            </div>
          </div>
          <Button onClick={() => setShowNewSite(!showNewSite)} className='bg-white text-black hover:bg-gray-200' data-testid='new-website-button'>
            <Sparkle size={20} weight='bold' className='mr-2' /> New Website
          </Button>
        </div>

        <AnimatePresence>
          {showNewSite && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className='mt-4 p-4 border border-border bg-surface' data-testid='new-website-form'>
              <form onSubmit={generateWebsite} className='space-y-3'>
                <Input value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })} placeholder='Website name (e.g., My Spiritual Journey)' className='bg-transparent border-border' data-testid='website-name-input' />
                <Textarea value={newSite.description} onChange={(e) => setNewSite({ ...newSite, description: e.target.value })} placeholder='Describe your website...' className='bg-transparent border-border min-h-[100px]' data-testid='website-description-input' />
                <div className='flex items-center gap-2'>
                  <input type='checkbox' checked={newSite.use_ai} onChange={(e) => setNewSite({ ...newSite, use_ai: e.target.checked })} className='w-4 h-4' />
                  <label className='text-sm'>Use AI to generate complete website code</label>
                </div>
                <Button type='submit' disabled={generating} className='w-full bg-white text-black hover:bg-gray-200' data-testid='generate-website-button'>
                  {generating ? 'Generating Website...' : 'Generate Website with AI'}
                </Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        <div className='mt-4 p-3 border border-border/50 bg-black/30'>
          <div className='flex items-center gap-2 mb-2'>
            <Rocket size={16} className='text-muted-foreground' />
            <label className='text-xs font-medium'>Netlify Token (for deployment)</label>
          </div>
          <Input type='password' value={netlifyToken} onChange={(e) => setNetlifyToken(e.target.value)} placeholder='Paste your Netlify Personal Access Token' className='bg-transparent border-border text-xs' data-testid='netlify-token-input' />
          <a href='https://app.netlify.com/user/applications#personal-access-tokens' target='_blank' rel='noopener noreferrer' className='text-xs text-blue-500 hover:underline mt-1 inline-block'>
            Get free token at Netlify.com
          </a>
        </div>
      </div>

      <div className='flex-1 flex overflow-hidden'>
        <WebsiteList websites={websites} selectedWebsite={selectedWebsite} onSelectWebsite={setSelectedWebsite} />
        <div className='flex-1 overflow-y-auto p-6' data-testid='website-details'>
          <WebsiteDetails selectedWebsite={selectedWebsite} deploying={deploying} onDeploy={deployToNetlify} onDelete={deleteWebsite} />
        </div>
      </div>
    </div>
  );
};

export default NetlifyBuilder;
